import { EventEmitter, Output } from '@angular/core';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  Injector,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NgControl,
} from '@angular/forms';
import { DropDown } from '@interfaces/drop-down.interface';
import { DropDownListComponent } from '@syncfusion/ej2-angular-dropdowns';
import { EmitType } from '@syncfusion/ej2-base';
import { FilteringEventArgs } from '@syncfusion/ej2-dropdowns';
import { TankSetupInventoryUnitsChooserComponent } from 'src/app/page/tank/components/tank-setup/widgets/tank-setup-inventory-units-chooser/tank-setup-inventory-units-chooser.component';

export const DROPDOWN_FORM_CONTROL_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => DropdownFormFieldComponent),
  multi: true,
};

@Component({
  selector: 'app-dropdown-form-field',
  templateUrl: './dropdown-form-field.component.html',
  styleUrls: ['./dropdown-form-field.component.scss'],
  providers: [DROPDOWN_FORM_CONTROL_ACCESSOR],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DropdownFormFieldComponent
  implements ControlValueAccessor, OnChanges, AfterViewInit, OnInit
{
  @ViewChild('dropdownField') dropDownField: DropDownListComponent;
  @Input() placeholder: string;
  @Input() fields: Object = { value: 'value', text: 'text' };
  @Input() noRecordsTemplate: string = 'General.NoRecordsFound';
  @Input() fieldLabel: string = 'General.Select';
  @Input() enableVirtualization: boolean = true;
  @Output() selectionChange: EventEmitter<any> = new EventEmitter<any>();
  @Input() debugMode: boolean = false;
  @Input() allowFiltering: boolean = true;
  //Optional Syncfusion display templates. Left undefined the control falls back to
  //its plain text rendering. For user pickers pass templates built on <app-user-option>.
  @Input() itemTemplate: any = undefined;
  @Input() valueTemplate: any = undefined;

  /**
   * Aliases for the two inputs above. Inside the templated <ejs-dropdownlist> the
   * reference variables #itemTemplate / #valueTemplate - whose names Syncfusion's
   * ContentChild predicates require verbatim - shadow the identically named component
   * properties, so the template cannot reach the inputs by their own names.
   */
  get customItemTemplate(): any {
    return this.itemTemplate;
  }

  get customValueTemplate(): any {
    return this.valueTemplate;
  }

  /** Picks which of the two <ejs-dropdownlist> instances renders. */
  get hasCustomTemplate(): boolean {
    return !!(this.itemTemplate || this.valueTemplate);
  }

  /** Fallback for whichever half of the pair a templated caller did not supply. */
  defaultText(data: any): string {
    const textField = (this.fields as any)?.text ?? 'text';
    return data ? data[textField] : '';
  }
  @Output() onFilter = new EventEmitter<any>();

  initialValue: any;
  selectedValue: any;
  selectedItem: any;
  showClearButton: any;
  /**
   * Shows a spinner over the control and stops it being opened, for a dropdown whose OPTIONS are
   * still being fetched.
   *
   * Distinct from `disabled`: disabled says "you may not change this", loading says "there is
   * nothing to choose from yet". A caller that wants both passes both; loading alone is enough to
   * keep the control shut, because an empty list has nothing to offer.
   *
   * This was a plain field nothing assigned, paired with a .spinner-overlay that had no CSS - the
   * markup for this existed and could never render.
   */
  @Input() loading: boolean = false;

  isDataBound: boolean = false;
  valueSet: boolean =  true;
  ngControl: NgControl;

  private _disabled: boolean = false;
  private _options: DropDown<string>[] = [];
  disabled: boolean;

  public get options(): DropDown<string>[] {
    return this._options;
  }
  @Input()
  public set options(value: DropDown<string>[]) {
    this._options = value;
  }

  get formControlName(): string | number | null {
    return this.ngControl?.name || null;
  }

  constructor(
    private injector: Injector,
    private _cdr: ChangeDetectorRef,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.options) {
      if (this.dropDownField) {
        this.dropDownField.dataSource = this.options as any;
        this.setSelectedValue();
      }
    }
  }

  ngAfterViewInit(): void {
    if(this.options)
      this.updateDropDownOptions();

    this.setSelectedValue();
    this._cdr.detectChanges();
  }

  private updateDropDownOptions() {
    this.dropDownField.dataSource = this.options as any;
  }

  ngOnInit(): void {
    //if (this.debugMode) debugger;

    this.ngControl = this.injector.get(NgControl, null);
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  writeValue(value: any): void {
    this.valueSet = false;
    this.initialValue = value;
    this.setSelectedValue();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    // Handle the disabled state
    this.disabled = isDisabled;
    this._cdr.markForCheck();
  }

  onDropdownChange($event: any): void {
    if(!$event.isInteracted)
      return;

    const value = $event.itemData.value;
    this.selectedValue = value;

    if (this.onChange) {
      this.onChange(value);
      this.selectionChange.emit($event?.itemData);
    }
  }

  private onChange: (value: any) => void;
  private onTouched: () => void;

  private setSelectedValue(): void {
    console.log('setSelectedValue called with initialValue:', this.initialValue);
    if (
      this.options &&
      this.options.length > 0 &&
      !this.valueSet &&
      this.dropDownField
    ) {
      this.selectedValue = this.initialValue;
      this.initialValue = null;
      if (this.selectedValue == null) {
        this.dropDownField.index = null;
        this.dropDownField.text = null;
      }
      this.dropDownField.value = this.selectedValue;
      this.dropDownField.refresh();
      this.valueSet = true;

      return;
    }

    if (!this.valueSet && this.initialValue === null && this.selectedValue) {
      this.selectedValue = this.initialValue;
      this.dropDownField.index = null;
      this.dropDownField.value = null;
      this.dropDownField.text = null;
      this.valueSet = true;
      this.dropDownField.refresh();
      return;
    }
  }

  private setControlDisableState() {
    if (this._disabled) this.ngControl?.control?.disable();
    else this.ngControl?.control?.enable();
  }

  public onFiltering: EmitType<FilteringEventArgs> = (e: FilteringEventArgs) => {
    // if we have defined a custom filter, emit the event
    this.onFilter.emit(e);
  }
}
